import { Controller } from "@hotwired/stimulus"
import flash from "../lib/flashes.js"

export default class extends Controller {
  static targets = ["item"]
  static values = {
    canDrag: {type: Boolean, default: false}
  }

  connect() {
    if (!this.canDragValue) return
    
    // Make photos draggable
    this.element.querySelectorAll('.photos__item[data-photos-drag-target]').forEach(photo => {
      photo.setAttribute('draggable', 'true')
      
      // Add mousedown/mouseup for cursor feedback
      photo.addEventListener('mousedown', () => {
        photo.classList.add('photos__item--grabbing')
      })
      
      // Handle mouseup both on the element and globally
      const handleMouseUp = () => {
        photo.classList.remove('photos__item--grabbing')
        document.removeEventListener('mouseup', handleMouseUp)
      }
      photo.addEventListener('mouseup', handleMouseUp)
      document.addEventListener('mouseup', handleMouseUp)
    })

    // Make folders draggable and droppable
    this.element.querySelectorAll('[data-folder-id]').forEach(folder => {
      folder.setAttribute('draggable', 'true')
    })
  }

  // Helper method to get bulk selector controller
  getBulkSelectorController() {
    // The bulk selector controller is on the photos__container element, which is the parent of photos__list
    const photosContainer = this.element.closest('.photos__container')
    if (photosContainer) {
      return this.application.getControllerForElementAndIdentifier(photosContainer, 'bulk-selector')
    }
    return null
  }

  // Stimulus actions for photo items
  dragStart(event) {
    const photo = event.currentTarget
    const photoSlug = photo.dataset.photoId
    const bulkSelectorController = this.getBulkSelectorController()
    
    // Check if this photo is part of a bulk selection
    if (bulkSelectorController && bulkSelectorController.hasSelection) {
      const selectedIds = bulkSelectorController.selectedIds
      
      // Get the photo ID from the checkbox for comparison with selected IDs
      const checkbox = photo.querySelector('.bulk-select-checkbox')
      const photoId = checkbox ? checkbox.dataset.itemId : null
      
      // If the dragged photo is selected, drag all selected photos
      if (photoId && selectedIds.includes(photoId)) {
        // Get all selected photo slugs for the API call
        const selectedSlugs = selectedIds.map(id => {
          const selectedCheckbox = document.querySelector(`input[data-item-id="${id}"]`)
          const selectedPhotoElement = selectedCheckbox ? selectedCheckbox.closest('[data-photo-id]') : null
          return selectedPhotoElement ? selectedPhotoElement.dataset.photoId : null
        }).filter(slug => slug !== null)
        
        event.dataTransfer.setData('text/plain', JSON.stringify(selectedSlugs))
        event.dataTransfer.setData('type', 'bulk-photos')
        
        // Add dragging class to all selected photos
        selectedIds.forEach(id => {
          const selectedCheckbox = document.querySelector(`input[data-item-id="${id}"]`)
          const selectedPhoto = selectedCheckbox ? selectedCheckbox.closest('[data-photo-id]') : null
          if (selectedPhoto) {
            selectedPhoto.classList.add('photos__item--dragging')
          }
        })
      } else {
        // Dragging a non-selected photo, treat as single photo
        event.dataTransfer.setData('text/plain', photoSlug)
        event.dataTransfer.setData('type', 'photo')
        photo.classList.add('photos__item--dragging')
      }
    } else {
      // No bulk selection, treat as single photo
      event.dataTransfer.setData('text/plain', photoSlug)
      event.dataTransfer.setData('type', 'photo')
      photo.classList.add('photos__item--dragging')
    }
  }

  dragEnd(event) {
    const photo = event.currentTarget
    const bulkSelectorController = this.getBulkSelectorController()
    
    // Remove dragging class from all photos
    if (bulkSelectorController && bulkSelectorController.hasSelection) {
      const selectedIds = bulkSelectorController.selectedIds
      selectedIds.forEach(id => {
        const selectedCheckbox = document.querySelector(`input[data-item-id="${id}"]`)
        const selectedPhoto = selectedCheckbox ? selectedCheckbox.closest('[data-photo-id]') : null
        if (selectedPhoto) {
          selectedPhoto.classList.remove('photos__item--dragging')
        }
      })
    }
    
    photo.classList.remove('photos__item--dragging')
  }

  dragOver(event) {
    // This method is called by the data-action but doesn't need to do anything
    // The actual drag over handling is done by the folder event listeners
  }

  drop(event) {
    // This method is called by the data-action but doesn't need to do anything
    // The actual drop handling is done by the folder event listeners
  }

  // Stimulus actions for folder items
  folderDragStart(event) {
    const folder = event.currentTarget
    event.dataTransfer.setData('text/plain', folder.dataset.folderId)
    event.dataTransfer.setData('type', 'folder')
    folder.classList.add('folder-dragging')
  }

  folderDragEnd(event) {
    const folder = event.currentTarget
    folder.classList.remove('folder-dragging')
  }

  folderDragOver(event) {
    event.preventDefault()
    const dragType = event.dataTransfer.types.includes('type') ? event.dataTransfer.getData('type') : null
    
    // Don't allow dropping a folder into itself or its descendants
    if (dragType === 'folder') {
      const draggedFolderId = event.dataTransfer.getData('text/plain')
      const targetFolder = event.currentTarget.closest('[data-folder-id]')
      if (draggedFolderId === targetFolder.dataset.folderId || 
          targetFolder.querySelector(`[data-folder-id="${draggedFolderId}"]`)) {
        return
      }
    }
    event.currentTarget.classList.add('folder-dragover')
  }

  folderDragLeave(event) {
    event.currentTarget.classList.remove('folder-dragover')
  }

  folderDrop(event) {
    event.preventDefault()
    event.currentTarget.classList.remove('folder-dragover')
    const dragType = event.dataTransfer.getData('type')
    if (dragType === 'folder') {
      this.handleFolderToFolderDrop(event)
    } else {
      this.handleFolderDrop(event)
    }
  }

  handleFolderToFolderDrop(event) {
    const folderId = event.dataTransfer.getData('text/plain')
    if (!folderId) return

    const targetFolderElement = event.currentTarget.closest('[data-folder-id]')
    const draggedFolder = document.querySelector(`[data-folder-id="${folderId}"]`)

    // Don't allow dropping a folder into itself or its descendants
    if (folderId === targetFolderElement.dataset.folderId || 
        targetFolderElement.querySelector(`[data-folder-id="${folderId}"]`)) {
      return
    }

    // Remove the dragged folder immediately for better UX
    if (draggedFolder) {
      draggedFolder.remove()
    }

    fetch(`/folders/${folderId}/update_parent`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({
        parent_id: targetFolderElement.dataset.folderId
      })
    }).then(response => response.json())
    .then(data => {
      if (data.success) {
        flash("Folder moved", "success")
      } else {
        console.error('Moving folder failed')
        window.location.reload()
      }
    })
  }

  handleFolderDrop(event) {
    event.preventDefault()
    event.currentTarget.classList.remove('folder-dragover')
    
    const dragType = event.dataTransfer.getData('type')
    const data = event.dataTransfer.getData('text/plain')
    
    if (!data) return

    const folderElement = event.currentTarget.closest('[data-folder-id]')
    const folderId = folderElement.dataset.folderId

    if (dragType === 'bulk-photos') {
      // Handle bulk photo drop
      this.handleBulkPhotoDrop(JSON.parse(data), folderId, folderElement)
    } else {
      // Handle single photo drop (existing functionality)
      this.handleSinglePhotoDrop(data, folderId, folderElement)
    }
  }

  handleSinglePhotoDrop(photoId, folderId, folderElement) {
    const countElement = folderElement.querySelector('.photos__item__count')
    const draggedItem = document.querySelector(`[data-photo-id="${photoId}"]`)

    // Remove the dragged item immediately for better UX
    if (draggedItem) {
      draggedItem.remove()
    }

    fetch(`/photos/${photoId}/update_folder`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({
        folder_id: folderId
      })
    }).then(response => response.json())
    .then(data => {
      if (data.success) {
        // Update the folder count
        if (countElement) {
          countElement.textContent = ` (${data.folder_count})`
        }

        const newPhotosContainer = document.querySelector('.photos__container')
        if (newPhotosContainer) {
          // Force Stimulus to reload the bulk selector and floating action menu controllers in the updated container so they have updated states
          const bulkSelectorController = this.application.getControllerForElementAndIdentifier(newPhotosContainer, 'bulk-selector')
          const floatingMenuController = this.application.getControllerForElementAndIdentifier(newPhotosContainer, 'floating-action-menu')
          
          if (bulkSelectorController) {
            bulkSelectorController.disconnect()
            bulkSelectorController.connect()
          }
          
          if (floatingMenuController) {
            floatingMenuController.disconnect()
            floatingMenuController.connect()
          }
        }

        flash("Photo moved", "success")
      } else {
        console.error('Moving to folder failed')
        window.location.reload()
      }
    })
  }

  handleBulkPhotoDrop(photoIds, folderId, folderElement) {
    const countElement = folderElement.querySelector('.photos__item__count')
    const bulkSelectorController = this.getBulkSelectorController()
    
    // Remove all dragged items immediately for better UX
    photoIds.forEach(photoSlug => {
      const draggedItem = document.querySelector(`[data-photo-id="${photoSlug}"]`)
      if (draggedItem) {
        draggedItem.remove()
      }
    })

    // Use the bulk move endpoint
    fetch('/photos/bulk_move', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({
        photo_ids: photoIds,
        folder_id: folderId
      })
    }).then(response => response.json())
    .then(data => {
      if (data.success) {
        // Update the folder count
        if (countElement) {
          countElement.textContent = ` (${data.folder_count})`
        }

        // Clear the selection since photos were moved
        if (bulkSelectorController) {
          bulkSelectorController.clearSelection()
        }

        const photoCount = photoIds.length
        flash(`${photoCount} photo${photoCount > 1 ? 's' : ''} moved`, "success")
      } else {
        console.error('Bulk moving to folder failed:', data.error)
        window.location.reload()
      }
    }).catch(error => {
      console.error('Bulk moving to folder failed:', error)
      window.location.reload()
    })
  }
}
